import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Wraps React.lazy() with automatic retry/reload on chunk load failure.
 *
 * After a deployment, old chunk files (e.g., CampaignsPage-CR92qFGO.js) are replaced
 * with new ones. If a user has the app open and navigates to a lazy-loaded route,
 * the browser tries to fetch the old chunk which no longer exists. The server returns
 * index.html (SPA fallback), causing a MIME type error.
 *
 * This utility catches that error and reloads the page to get fresh chunks.
 * Uses sessionStorage to prevent infinite reload loops.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  chunkName: string
): LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = `chunk_reload_${chunkName}`;
    const hasReloaded = sessionStorage.getItem(storageKey);

    try {
      const module = await importFn();
      // Success - clear any reload flag
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      // Check if this is a chunk load error (stale deployment)
      const isChunkError =
        error instanceof Error &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
          error.message.includes('Loading chunk') ||
          error.message.includes('Loading CSS chunk') ||
          error.message.includes('text/html'));

      if (isChunkError && !hasReloaded) {
        // Mark that we're reloading to prevent infinite loop
        sessionStorage.setItem(storageKey, 'true');
        // eslint-disable-next-line no-console
        console.log(`[lazyWithRetry] Chunk "${chunkName}" failed to load, reloading page...`);
        window.location.reload();
        // Return a never-resolving promise since we're reloading
        return new Promise(() => {});
      }

      // If we've already reloaded once, or it's a different error, throw
      sessionStorage.removeItem(storageKey);
      throw error;
    }
  });
}
